import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Scale, Save, Loader2, Info } from 'lucide-react'
import { shipping } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

type WeightMode = 'fixed' | 'calculated'

export default function WeightConfigPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<WeightMode>('fixed')
  const [fixedWeight, setFixedWeight] = useState(0.5)
  const [hasChanges, setHasChanges] = useState(false)

  // Query para obtener configuración actual
  const configQuery = useQuery({
    queryKey: ['weight-config'],
    queryFn: () => shipping.getWeightConfig(),
  })

  // Cargar valores cuando llegan los datos
  useEffect(() => {
    if (configQuery.data) {
      setMode(configQuery.data.mode)
      setFixedWeight(configQuery.data.fixed_weight)
      setHasChanges(false)
    }
  }, [configQuery.data])

  // Mutation para guardar configuración
  const saveMutation = useMutation({
    mutationFn: () => shipping.saveWeightConfig({
      mode,
      fixed_weight: fixedWeight
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weight-config'] })
      toast.success('Configuracion guardada correctamente')
      setHasChanges(false)
    },
    onError: (error: Error) => {
      toast.error(`Error al guardar: ${error.message}`)
    }
  })

  const handleModeChange = (newMode: WeightMode) => {
    setMode(newMode)
    setHasChanges(true)
  }

  const handleWeightChange = (value: number) => {
    setFixedWeight(value)
    setHasChanges(true)
  }

  const handleSave = () => {
    if (mode === 'fixed' && fixedWeight < 0.1) {
      toast.error('El peso minimo debe ser 0.1 kg')
      return
    }
    saveMutation.mutate()
  }

  if (configQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuracion Pesos</h1>
            <p className="text-sm text-gray-500">
              Selecciona como calcular el peso de las etiquetas de envio
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saveMutation.isPending}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
            hasChanges && !saveMutation.isPending
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Guardar
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          El peso seleccionado se usara por defecto al generar etiquetas de envio.
          Siempre podras modificar el peso manualmente antes de crear cada envio.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4">
        {/* Option 1: Fixed Weight */}
        <label
          className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
            mode === 'fixed'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <div className="flex items-start gap-4">
            <input
              type="radio"
              name="weightMode"
              checked={mode === 'fixed'}
              onChange={() => handleModeChange('fixed')}
              className="mt-1 h-4 w-4 text-blue-600"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">Peso fijo</div>
              <p className="text-sm text-gray-600 mt-1">
                Todas las etiquetas se generaran con el mismo peso por defecto.
              </p>

              {mode === 'fixed' && (
                <div className="mt-4 flex items-center gap-3">
                  <input
                    type="number"
                    value={fixedWeight}
                    onChange={(e) => handleWeightChange(parseFloat(e.target.value) || 0.1)}
                    min="0.1"
                    step="0.1"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-gray-600">kg</span>
                </div>
              )}
            </div>
          </div>
        </label>

        {/* Option 2: Calculated Weight */}
        <label
          className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
            mode === 'calculated'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <div className="flex items-start gap-4">
            <input
              type="radio"
              name="weightMode"
              checked={mode === 'calculated'}
              onChange={() => handleModeChange('calculated')}
              className="mt-1 h-4 w-4 text-blue-600"
            />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">Peso real</div>
              <p className="text-sm text-gray-600 mt-1">
                El peso se calculara automaticamente sumando el peso de cada producto del pedido
                (segun los pesos guardados en PrestaShop).
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Formula: suma de (peso producto × cantidad) para todos los productos del pedido
              </p>
            </div>
          </div>
        </label>
      </div>

      {/* Current config info */}
      {configQuery.data?.updated_at && (
        <p className="text-sm text-gray-500">
          Ultima modificacion: {new Date(configQuery.data.updated_at).toLocaleString('es-ES')}
        </p>
      )}
    </div>
  )
}
